// Libs
import classNames from 'classnames/bind';
import { IconEndLine, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { useRef, useState, useEffect } from 'react';
import { OnProgressProps } from 'react-player/base';
// Components, Layouts, Pages
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { upsertModuleWatchProgress } from '~/thunks/module/moduleThunk';
import { moduleApi } from '~/apis/module';
import {
  ENGLISH_VIDEO_LESSON_SUBTITLE,
  ITALIAN_VIDEO_LESSON_SUBTITLE,
  LAST_SECONDS_TO_MARK_WATCHED,
  MAX_VIDEO_TIME_FORWARD,
  PROGRESS_POST_INTERVAL_MS,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './ModalExternalUrl.module.scss';
import { images } from '~/assets';

type Props = {
  isModalOpen: boolean;
  externalUrl: string | null;
  title?: string;
  courseId?: string;
  moduleId?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const ModalExternalUrl = (props: Props) => {
  //#region Destructuring Props
  const { isModalOpen, externalUrl, title, courseId, moduleId, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base']);
  const videoRef = useRef<ReactPlayer>(null);
  const [played, setPlayed] = useState(0);
  const hasSentWatchedRef = useRef(false);
  const dispatch = useAppDispatch();
  const lastSentAtRef = useRef<number>(0);
  const lastSentSecondsRef = useRef<number>(0);
  //#endregion Declare Hook

  //#region Declare State
  const isLessonVideo =
    title?.includes(ENGLISH_VIDEO_LESSON_SUBTITLE) || title?.includes(ITALIAN_VIDEO_LESSON_SUBTITLE);
  const getCurrentSeconds = () => Math.floor(videoRef?.current?.getCurrentTime() ?? 0);
  const getDurationSeconds = () => Math.floor(videoRef?.current?.getDuration() ?? 0);
  const isVideoUrl =
    externalUrl &&
    (externalUrl.toLowerCase().endsWith('.mp4') ||
      externalUrl.toLowerCase().endsWith('.webm') ||
      externalUrl.toLowerCase().endsWith('.ogg') ||
      externalUrl.toLowerCase().endsWith('.mov') ||
      externalUrl.toLowerCase().endsWith('.avi') ||
      externalUrl.toLowerCase().endsWith('.mkv') ||
      ReactPlayer.canPlay(externalUrl));
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden && isModalOpen) postProgressBeacon(getCurrentSeconds(), false);
    };
    const onBeforeUnload = () => {
      if (isModalOpen) postProgressBeacon(getCurrentSeconds(), false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, moduleId, isModalOpen]);
  //#endregion Implement Hook

  //#region Handle Function
  const postProgressBeacon = (lastWatchedSeconds: number, isWatched = false) => {
    if (!courseId || !moduleId || !isLessonVideo) return;
    const success = moduleApi.upsertModuleWatchProgressBeacon(Number(moduleId), {
      courseId: Number(courseId),
      lastWatchedSeconds: Math.max(0, Math.floor(lastWatchedSeconds)),
      ...(isWatched ? { isWatched: true } : {}),
    });
    //if beacon queue failed -> fallback via thunk
    if (!success) {
      postProgressThunk(lastWatchedSeconds, isWatched);
    }
  };

  const postProgressThunk = (lastWatchedSeconds: number, isWatched = false) => {
    if (!courseId || !moduleId || !isLessonVideo) return;

    dispatch(
      upsertModuleWatchProgress({
        moduleId: Number(moduleId),
        body: {
          courseId: Number(courseId),
          lastWatchedSeconds: Math.max(0, Math.floor(lastWatchedSeconds)),
          ...(isWatched ? { isWatched: true } : {}),
        },
      })
    );
  };

  const maybeSendProgress = (seconds: number) => {
    const now = Date.now();
    if (now - (lastSentAtRef.current || 0) >= PROGRESS_POST_INTERVAL_MS && seconds !== lastSentSecondsRef.current) {
      lastSentAtRef.current = now;
      lastSentSecondsRef.current = seconds;
      postProgressThunk(seconds, false);
    }
  };

  const handleProgress = (state: OnProgressProps) => {
    const current = videoRef?.current?.getCurrentTime() ?? 0;
    const duration = getDurationSeconds();
    const secondsLeft = duration - current;

    if (current <= played + MAX_VIDEO_TIME_FORWARD) {
      setPlayed(current);

      if (current > 0 && secondsLeft <= LAST_SECONDS_TO_MARK_WATCHED) {
        if (!hasSentWatchedRef.current) {
          postProgressThunk(duration, true);
          hasSentWatchedRef.current = true;
        }
        return;
      }

      maybeSendProgress(Math.floor(current));
      return;
    }

    videoRef?.current?.seekTo(played, 'seconds');
  };

  const handleSeek = (seconds: number) => {
    postProgressThunk(Math.floor(seconds), false);
  };

  const handlePause = () => {
    postProgressThunk(getCurrentSeconds(), false);
  };

  const handleEndedVideo = () => {
    setPlayed(0);
    const duration = getDurationSeconds();
    postProgressThunk(duration, true);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  //#endregion Handle Function

  if (!isModalOpen) return null;

  return (
    <div className={cx('modalOverlay')} onClick={handleBackdropClick}>
      <div className={cx('modalContent')}>
        <div className={cx('contentWrapper')}>
          <div className={cx('topBar')}>
            <View className={cx('buttonClose')}>
              <View as={'div'} onClick={onClose} className={cx('iconCloseModal')}>
                <IconEndLine />
              </View>
            </View>
          </div>
          {externalUrl && (
            <>
              {isVideoUrl ? (
                <div className={cx('videoContainer')}>
                  <ReactPlayer
                    ref={videoRef}
                    url={externalUrl}
                    onProgress={handleProgress}
                    onPause={handlePause}
                    onSeek={handleSeek}
                    onEnded={handleEndedVideo}
                    width='100%'
                    height='100%'
                    className={cx('videoPlayer')}
                    controls
                    config={{
                      file: {
                        attributes: {
                          poster: images.commonLogoFullPositiveThumbnail,
                          controlsList: 'nodownload noplaybackrate',
                          preload: 'metadata',
                          disablePictureInPicture: true,
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <iframe
                  src={externalUrl}
                  width='100%'
                  height='100%'
                  title={title || 'External Content'}
                  className={cx('iframe')}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalExternalUrl;
